import { format, isSameDay } from "date-fns";

import { Filter } from "../../components/FilterChips/FilterChips";

import { GetMeetingsSearchParams } from "../../api/services/defs/internal-api";

import { capitalizeWord } from "../../utils/stringUtils";

import { CompanyMeetingsFilters } from "./schema";

/**
 * Format meeting date time range in a user-friendly way
 */
export const getFormattedMeetingDateString = (startDateTime: Date, endDateTime: Date) => {
	const startDate = format(startDateTime, "MMM d, yyyy");
	const endDate = format(endDateTime, "MMM d, yyyy");
	const startTime = format(startDateTime, "h:mmaaa");
	const endTime = format(endDateTime, "h:mmaaa");

	if (!isSameDay(startDateTime, endDateTime)) {
		return `${startDate} • ${startTime} - ${endDate} • ${endTime}`;
	} else {
		return `${startDate} • ${startTime} - ${endTime}`;
	}
};

/**
 * Convert the filters from the UI into the filter chips for the UI.
 */
export const convertFiltersToFilterChips = (filters: CompanyMeetingsFilters) => {
	const filterChips: Filter[] = [];

	if (filters.participant) {
		filterChips.push({
			id: "participant",
			type: "single-employee",
			label: "Participant",
			value: { id: filters.participant.id, name: filters.participant.name },
		});
	}

	if (filters.dateRangeStart && filters.dateRangeEnd) {
		if (isSameDay(filters.dateRangeStart, filters.dateRangeEnd)) {
			filterChips.push({
				id: "dateRange",
				type: "string",
				label: "Meeting Date",
				value: format(filters.dateRangeStart, "MMM d, yyyy"),
			});
		} else {
			filterChips.push({
				id: "dateRange",
				type: "string",
				label: "Meeting Date",
				value:
					format(filters.dateRangeStart, "MMM d, yyyy") + " - " + format(filters.dateRangeEnd, "MMM d, yyyy"),
			});
		}
	}

	if (filters.status && filters.status.length > 0) {
		filterChips.push({
			id: "status",
			type: "string",
			label: "Status",
			value: filters.status.map((status) => capitalizeWord(status)).join(", "),
		});
	}

	return filterChips;
};

/**
 * Convert the filters from the UI into filter parameters for the search service.
 * This separates filter conversion from pagination and sorting.
 */
export const getMeetingsSearchServiceFilters = (filters: CompanyMeetingsFilters, searchValue?: string) => {
	const finalFilters: Partial<GetMeetingsSearchParams> = {
		isCompanyMeetingsRequest: true,
	};

	if (filters.participant) {
		finalFilters.participantId = filters.participant.id;
	}

	if (filters.dateRangeStart) {
		finalFilters.dateRangeStart = filters.dateRangeStart;
	}

	if (filters.dateRangeEnd) {
		finalFilters.dateRangeEnd = filters.dateRangeEnd;
	}

	if (filters.status && filters.status.length > 0) {
		finalFilters.status = filters.status;
	}

	if (searchValue) {
		finalFilters.name = searchValue;
	}

	return finalFilters;
};
